import { useEffect, useState } from "react";
import { FileDown, FileStack, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getParishes, buildParishes, downloadBriefing } from "@/lib/api";

export default function BriefingPanel({ params }) {
  const [parishes, setParishes] = useState([]);
  const [parish, setParish] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    getParishes().then((d) => {
      setParishes(d.parishes || []);
      if (d.parishes?.length) setParish(d.parishes[0].name);
      else if (d.status !== "running") buildParishes().catch(() => {});
    }).catch(() => {});
  }, []);

  const download = async (which) => {
    setBusy(which);
    try {
      await downloadBriefing({ ...params, parish: which });
      toast.success(which === "all" ? `All ${parishes.length} parish briefings downloaded` : `${which} briefing downloaded`);
    } catch (e) {
      toast.error("Could not generate briefing");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="briefing-panel" className="glass rounded-lg p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileStack className="w-4 h-4 text-cyan" />
          <h2 className="font-heading font-semibold uppercase tracking-wide text-sm text-slate-100">Parish Briefings</h2>
        </div>
        <span className="font-mono-t text-[9px] uppercase tracking-widest text-slate-500">PDF · 1 page each</span>
      </div>
      <Select value={parish} onValueChange={setParish} disabled={!parishes.length}>
        <SelectTrigger data-testid="briefing-parish-select" className="bg-slate-900/60 border-border font-mono-t text-xs h-9">
          <SelectValue placeholder={parishes.length ? "Select parish" : "Mapping parishes…"} />
        </SelectTrigger>
        <SelectContent>
          {parishes.map((p) => (
            <SelectItem key={p.name} value={p.name} data-testid={`briefing-parish-option-${p.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {p.name} · {p.asset_count} assets
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="briefing-download-parish" disabled={!parish || !!busy} onClick={() => download(parish)}
          className="flex items-center justify-center gap-1.5 bg-cyan text-slate-950 font-heading font-semibold uppercase tracking-wide text-xs py-2 rounded-md hover:bg-cyan/85 transition-colors disabled:opacity-50">
          {busy === parish ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Parish PDF
        </button>
        <button data-testid="briefing-download-all" disabled={!parishes.length || !!busy} onClick={() => download("all")}
          className="flex items-center justify-center gap-1.5 border border-cyan/50 text-cyan font-heading font-semibold uppercase tracking-wide text-xs py-2 rounded-md hover:bg-cyan/10 transition-colors disabled:opacity-50">
          {busy === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileStack className="w-3.5 h-3.5" />} All {parishes.length || ""}
        </button>
      </div>
      <div className="font-mono-t text-[9px] text-slate-500 leading-relaxed">
        Captures the hour on screen{params.drill ? " (DRILL)" : ""} plus the worst hour in the next 72h, wind arrival and assets needing action.
      </div>
    </div>
  );
}
